import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { CheckCircle2, Sparkles } from "lucide-react";
import { PERSONALIZATION_TEMPLATES, type PersonalizationTemplate } from "@/lib/personalizationTemplates";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface TemplateSelectorProps {
  onSelectTemplate: (template: PersonalizationTemplate) => void;
  onSkip: () => void;
}

export const TemplateSelector = ({ onSelectTemplate, onSkip }: TemplateSelectorProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const roleTemplates = PERSONALIZATION_TEMPLATES.filter(t => t.category === 'role');
  const industryTemplates = PERSONALIZATION_TEMPLATES.filter(t => t.category === 'industry');

  const handleSelect = (template: PersonalizationTemplate) => {
    setSelectedTemplate(template.id);
    // Auto-apply after a brief moment
    setTimeout(() => {
      onSelectTemplate(template);
    }, 300);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <Label className="text-base font-semibold">Schnellstart mit Template (optional)</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Wähle ein vordefiniertes Template basierend auf deiner Rolle oder Branche. Du kannst alle Werte später anpassen.
        </p>
      </div>

      <Tabs defaultValue="role" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="role">Nach Rolle</TabsTrigger>
          <TabsTrigger value="industry">Nach Branche</TabsTrigger>
        </TabsList>

        <TabsContent value="role" className="mt-6 space-y-3">
          {roleTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => handleSelect(template)}
              className={cn(
                "w-full flex items-start gap-4 rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.01] text-left",
                selectedTemplate === template.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
            >
              <div className="text-3xl flex-shrink-0">{template.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={cn(
                    "font-semibold text-base",
                    selectedTemplate === template.id ? "text-foreground" : "text-foreground/90"
                  )}>
                    {template.name}
                  </p>
                  {selectedTemplate === template.id && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {template.description}
                </p>
              </div>
            </button>
          ))}
        </TabsContent>

        <TabsContent value="industry" className="mt-6 space-y-3">
          {industryTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => handleSelect(template)}
              className={cn(
                "w-full flex items-start gap-4 rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.01] text-left",
                selectedTemplate === template.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
            >
              <div className="text-3xl flex-shrink-0">{template.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={cn(
                    "font-semibold text-base",
                    selectedTemplate === template.id ? "text-foreground" : "text-foreground/90"
                  )}>
                    {template.name}
                  </p>
                  {selectedTemplate === template.id && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {template.description}
                </p>
              </div>
            </button>
          ))}
        </TabsContent>
      </Tabs>

      <Button
        onClick={onSkip}
        variant="outline"
        className="w-full"
      >
        Überspringen & selbst ausfüllen
      </Button>
    </div>
  );
};